import { FC, useCallback, useEffect, useRef, useState } from "react";
import "./SessionCountdown.css";
import { selectClient } from "@store/client/clientReducer";
import { useAppSelector } from "@store/store";
import { SessionCountdownButton } from "./SessionCountdownButton";
import { useTranslation } from "react-i18next";
import { useSessionReset } from "../../../hooks/useSessionReset/useSessionReset";

const SESSION_COUNT_DOWN_SECONDS = 10;

export const SessionCountdown: FC = () => {
  const client = useAppSelector(selectClient);
  const [isCountdown, setIsCountdown] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(
    SESSION_COUNT_DOWN_SECONDS,
  );
  const [lastActivityTimestamp, setLastActivityTimestamp] = useState<number>();
  const pageInitializedTimestamp = useRef(Date.now());
  const idleRefreshSeconds = client.kioskFeature.secondsBeforeSessionReset;
  const pageRefreshSeconds = 60 * 60; // 1 hr
  const { t } = useTranslation();
  const resetSession = useSessionReset();

  const onUserActivity = useCallback(() => {
    setLastActivityTimestamp(Date.now());
  }, []);

  useEffect(() => {
    const handleActivity = () => {
      onUserActivity();
    };

    document.addEventListener("mousemove", handleActivity);
    document.addEventListener("mousedown", handleActivity);
    document.addEventListener("keypress", handleActivity);
    document.addEventListener("touchmove", handleActivity);

    return () => {
      document.removeEventListener("mousemove", handleActivity);
      document.removeEventListener("mousedown", handleActivity);
      document.removeEventListener("keypress", handleActivity);
      document.removeEventListener("touchmove", handleActivity);
    };
  }, [onUserActivity]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();

      if (
        !lastActivityTimestamp &&
        (now - pageInitializedTimestamp.current) / 1000 >= pageRefreshSeconds
      ) {
        // hard refresh kiosk at least once an hour to bring in new data if no user
        // activity, countdown handles case with user activity
        resetSession(true);
      }
      if (
        lastActivityTimestamp &&
        (now - lastActivityTimestamp) / 1000 > idleRefreshSeconds &&
        !isCountdown
      ) {
        // the page has been idle since last user activity, show countdown
        setIsCountdown(true);
        setCountdownSeconds(SESSION_COUNT_DOWN_SECONDS);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [
    lastActivityTimestamp,
    isCountdown,
    idleRefreshSeconds,
    resetSession,
    pageRefreshSeconds,
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isCountdown) {
        setCountdownSeconds((prev) => prev - 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isCountdown, countdownSeconds, resetSession]);

  useEffect(() => {
    if (isCountdown && countdownSeconds < 1) {
      resetSession();
    }
  }, [isCountdown, countdownSeconds, resetSession]);

  const handleReset = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      resetSession();
    },
    [resetSession],
  );

  const handleContinue = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsCountdown(false);
  }, []);

  if (!isCountdown) {
    return null;
  }
  return (
    <div
      className="kiosk-session-countdown"
      data-testid="kiosk-session-countdown"
      onClick={handleContinue}>
      <div className="kiosk-session-countdown-container">
        <div className="kiosk-session-countdown-message">
          {t("kiosk.sessionCountdown.message", { seconds: countdownSeconds })}
        </div>
        <div className="kiosk-session-countdown-number">{countdownSeconds}</div>
        <div className="kiosk-session-countdown-controls">
          <SessionCountdownButton
            label={t("kiosk.sessionCountdown.continuePlanning")}
            onPress={handleContinue}
          />
          <SessionCountdownButton
            label={t("kiosk.sessionCountdown.startNewPlan")}
            inverse
            onPress={handleReset}
          />
        </div>
      </div>
    </div>
  );
};
